/**
 * MovementsPanel — shows register movements created by a document.
 *
 * Groups movements by register with columns: period, movement_type, dimensions, resources.
 */
import React, { useEffect, useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { ApiService } from '@/utils/api'

interface ColumnMeta {
  name: string
  title: string
  group: 'dimension' | 'resource' | 'detail'
}

interface RegisterMovements {
  register_name: string
  register_synonym: string
  register_type: string
  columns: ColumnMeta[]
  movements: Record<string, any>[]
}

interface MovementsPanelProps {
  connectionId: string
  objectName: string
  recordId: string
  reloadKey?: number
}

const GROUP_COLORS: Record<string, string> = {
  dimension: 'info.main',
  resource: 'success.main',
  detail: 'text.secondary',
}

export function MovementsPanel({ connectionId, objectName, recordId, reloadKey = 0 }: MovementsPanelProps) {
  const [data, setData] = useState<RegisterMovements[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await ApiService.get<{ data: RegisterMovements[] }>(
        `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}/movements`,
      )
      setData(resp.data)
    } catch {
      setData([])
    }
    setLoading(false)
  }, [connectionId, objectName, recordId])

  useEffect(() => { load() }, [load, reloadKey])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <Typography variant="body2">No register movements for this document</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {data.map((reg) => (
        <Box key={reg.register_name}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {reg.register_synonym}
            </Typography>
            <Chip
              label={reg.register_type === 'register_accumulation' ? 'accumulation' : 'info'}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 10 }}
            />
            <Chip
              label={`${reg.movements.length} row${reg.movements.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 20, fontSize: 10 }}
            />
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    Period
                  </TableCell>
                  {reg.register_type === 'register_accumulation' && (
                    <TableCell sx={{ fontWeight: 600, fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      Type
                    </TableCell>
                  )}
                  {reg.columns.map((col) => (
                    <TableCell
                      key={col.name}
                      sx={{
                        fontWeight: 600,
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        color: GROUP_COLORS[col.group] || 'text.secondary',
                      }}
                    >
                      {col.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reg.movements.map((mov, i) => (
                  <TableRow key={mov.id || i} hover>
                    <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {mov.period ? new Date(mov.period).toLocaleString() : '—'}
                    </TableCell>
                    {reg.register_type === 'register_accumulation' && (
                      <TableCell>
                        <Chip
                          label={mov.movement_type || '—'}
                          size="small"
                          color={mov.movement_type === 'receipt' ? 'success' : mov.movement_type === 'expense' ? 'error' : 'default'}
                          sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                        />
                      </TableCell>
                    )}
                    {reg.columns.map((col) => (
                      <TableCell key={col.name} sx={{ fontSize: 12 }}>
                        {mov[col.name] != null ? String(mov[col.name]) : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  )
}
